import {isNil} from "lodash";
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import {AutoSizer, Collection, Grid} from "react-virtualized";
import useDragEvents from "../../hooks/useDragEvents";
import "./_sql-table.scss";
import SQLTableFilterComponent from "./SQLTableFilterComponent";
import useTableData from "./useTableData";
import moment from "moment";
import {FilterTypes} from "../../constans/filter";

const ROW_HEIGHT = 50;
const MIN_COLUMN_WIDTH = 75;

const rangeRegex = new RegExp(
    "([\\[\\(])\\s*(-?\\d+)\\s*-\\s*(-?\\d+)\\s*([\\]\\)])"
);
const operationRegex = new RegExp("(>=|<=|>|<|=|<>)(-?\\d+)");

const dateRegex = new RegExp(
    "(\\d{2,4})[-\\.\\/](\\d{2})[-\\.\\/](\\d{2,4})",
    "g"
);

const operationDateRegex = new RegExp(
    "(>=|<=|>|<|=|<>)(\\d{2,4})[-\\.\\/](\\d{2})[-\\.\\/](\\d{2,4})"
);

const yearFirstFormats = ["YYYY/MM/DD", "YYYY.MM.DD", "YYYY-MM-DD"];
const monthFirstFormats = ["MM/DD/YYYY", "MM.DD.YYYY", "MM-DD-YYYY"];
const dayFirstFormats = ["DD/MM/YYYY", "DD.MM.YYYY", "DD-MM-YYYY"];

function tryToParseDate(string, formats) {
    for (let format of formats) {
        const parsed = moment(string, format);
        if (parsed.isValid()) return parsed;
    }
    return null;
}

function parseDate(string) {
    if (
        string.split("/")[0].length === 4 ||
        string.split("-")[0].length === 4 ||
        string.split(".")[0].length === 4
    ) {
        const parsed = tryToParseDate(string, yearFirstFormats);
        if (parsed) return parsed;
    }
    if (moment.localeData().longDateFormat("L").startsWith("MM")) {
        const parsed = tryToParseDate(string, monthFirstFormats);
        if (parsed) return parsed;
    } else {
        const parsed = tryToParseDate(string, dayFirstFormats);
        if (parsed) return parsed;
    }
    return null;
}

function SQLTableHeaderCellHandle({container, onResize, index}) {
    const drag = useRef();

    const onDrag = useCallback(
        (e) => {
            const rect = container.current.getBoundingClientRect();
            let width = e.clientX - rect.left;
            if (width < MIN_COLUMN_WIDTH) width = MIN_COLUMN_WIDTH;
            onResize(index, width);
        },
        [onResize, index, container]
    );

    useDragEvents(drag, onDrag);

    return <span className="sql-table-header-size-handle" ref={drag} />;
}

function SQLTableHeaderFilter({filters, setFilters, header}) {
    const container = useRef();

    const [showFilter, setShowFilter] = useState(false);

    const onClick = useCallback(() => {
        setShowFilter((prev) => !prev);
    }, []);

    const hasFilter = useMemo(() => {
        return !!filters[header.field];
    }, [header, filters]);

    return (
        <div
            className={`sql-table-header-filter${hasFilter ? " has-filter" : ""}`}
            ref={container}>
            <i className="fa-solid fa-filter" onClick={onClick} />
            <SQLTableFilterComponent
                show={showFilter}
                parent={container}
                setShowFilter={setShowFilter}
                filters={filters}
                setFilters={setFilters}
                header={header}
            />
        </div>
    );
}

function SQLTableHeaderCell({
    header,
    style,
    isLast,
    index,
    onResize,
    container,
    sortType,
    setSort,
    sortIndex,
    filters,
    setFilters,
}) {
    const className = useMemo(() => {
        let className = "sql-table-header";
        if (isLast) className += " last";
        if (sortType) className += " sorted";
        if (!setSort) className += " not-sortable";
        if (header.children) className += " with-children";
        return className;
    }, [isLast, sortType, setSort, header]);

    const onTextClick = useCallback(() => {
        if (!setSort) return;
        setSort((prev) => {
            const copy = prev.slice();
            const index = copy.findIndex(
                (item) => item.column === header.field
            );
            if (index === -1) {
                copy.push({column: header.field, type: "ASC"});
            } else {
                if (copy[index].type === "ASC") {
                    copy[index].type = "DESC";
                } else {
                    copy.splice(index, 1);
                }
            }
            return copy;
        });
    }, [setSort, header.field]);

    return (
        <div style={style} className={className} title={header.name}>
            <div onClick={onTextClick}>
                {header.headerComponent?.(header.name) ?? header.name}
            </div>
            {!header.children && (
                <>
                    <div className="d-flex align-items-center">
                        {setSort && sortIndex !== -1 && (
                            <div className="sql-table-header-sort-icon">
                                {sortType === "ASC" && (
                                    <i className="fa-solid fa-arrow-up-wide-short" />
                                )}
                                {sortType === "DESC" && (
                                    <i className="fa-solid fa-arrow-down-wide-short" />
                                )}
                                <span>({sortIndex + 1})</span>
                            </div>
                        )}
                        <SQLTableHeaderFilter
                            filters={filters}
                            setFilters={setFilters}
                            header={header}
                        />
                    </div>
                    <SQLTableHeaderCellHandle
                        container={container}
                        index={index}
                        onResize={onResize}
                    />
                </>
            )}
        </div>
    );
}

function SQLTableItemCell({
    style,
    header: _header,
    row,
    isLast,
    rowIndex,
    colIndex,
    onClick,
    getCellClassName,
    isVertical,
}) {
    const header = useMemo(() => {
        if (!isVertical || !row?.headerRef || _header.frozen || _header.isIndex)
            return _header;
        return {
            ..._header,
            valueFormatter: row.headerRef.valueFormatter,
            component: row.headerRef.component,
        };
    }, [isVertical, _header, row]);

    const title = useMemo(() => {
        const {field, valueFormatter, component} = header;
        if (component) return null;
        if (valueFormatter) return valueFormatter(row[field]);
        return row[field];
    }, [header, row]);

    const value = useMemo(() => {
        const {field, valueFormatter, component} = header;
        if (component) return component(row[field]);
        if (valueFormatter) return valueFormatter(row[field]);
        return row[field];
    }, [header, row]);

    const className = useMemo(() => {
        let className = "sql-table-cell";
        if (isLast) className += " last";
        if (onClick) className += " click";
        if (getCellClassName) {
            let additionalClass = getCellClassName(row, header);
            if (additionalClass) className += ` ${additionalClass}`;
        }
        return className;
    }, [isLast, getCellClassName, row, onClick, header]);

    const onMouseEnter = useCallback(
        (e) => {
            if (isVertical) {
                const colIndex = e.target.getAttribute("data-col");
                const parent =
                    e.target.parentElement.parentElement.parentElement
                        .parentElement.parentElement;
                const cols = parent.querySelectorAll(
                    `[data-col="${colIndex}"]`
                );
                cols.forEach((row) => row.classList.add("hover"));
            } else {
                const rowIndex = e.target.getAttribute("data-row");
                const parent =
                    e.target.parentElement.parentElement.parentElement
                        .parentElement.parentElement;
                const rows = parent.querySelectorAll(
                    `[data-row="${rowIndex}"]`
                );
                rows.forEach((row) => row.classList.add("hover"));
            }
        },
        [isVertical]
    );

    const onMouseLeave = useCallback((e) => {
        const parent =
            e.target.parentElement.parentElement.parentElement.parentElement
                .parentElement;
        const hoveredRows = parent.querySelectorAll(".hover");
        hoveredRows.forEach((row) => row.classList.remove("hover"));
    }, []);

    const onCellClick = useCallback(() => {
        if (!onClick) return;
        onClick(row, header);
    }, [onClick, row, header]);

    return (
        <div
            style={style}
            className={className}
            title={title}
            data-row={rowIndex}
            data-col={colIndex}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onCellClick}>
            <div>{value}</div>
        </div>
    );
}

const SQLTableGridRenderer = forwardRef(function SQLTableGridRenderer(
    {
        showIndex = false,
        headers,
        width,
        sort,
        onResize: onResizeProps,
        container,
        setSort,
        offset = 0,
        columnSize,
        scrollLeft,
        headerOffset = 0,
        data,
        sortedData,
        height,
        onScroll,
        scrollTop,
        onClick,
        getCellClassName,
        onlyHeaders,
        headersHeight,
        isVertical,
        filters,
        setFilters,
    },
    ref
) {
    const headerApi = useRef();
    const gridApi = useRef();

    const onResize = useCallback(
        (index, width) => {
            onResizeProps(
                index,
                width,
                gridApi.current?._scrollingContainer?.scrollLeft || 0
            );
        },
        [onResizeProps]
    );

    const getColumnSize = useCallback(
        ({index}) => columnSize[index] || MIN_COLUMN_WIDTH,
        [columnSize]
    );

    const headerGrid = useMemo(() => {
        const tmp = [];
        let x = 0;
        let maxY = headersHeight > ROW_HEIGHT ? headersHeight / ROW_HEIGHT : 1;
        if (!onlyHeaders && showIndex) {
            const width = getColumnSize({index: 0});
            tmp.push({
                header: null,
                x,
                y: 0,
                width,
                height: maxY * ROW_HEIGHT,
                index: 0,
            });
            x += width;
        }
        for (let i = 0; i < headers.length; i++) {
            const index = !onlyHeaders && showIndex ? i + 1 : i;
            const header = headers[i];
            const prevHeader = headers[i - 1];
            const width = getColumnSize({index});
            if (header.parent) {
                if (prevHeader?.parent !== header.parent) {
                    let parentWidth = 0;
                    for (let j = 0; j < header.parent.children.length; j++) {
                        parentWidth += getColumnSize({index: index + j});
                    }
                    tmp.push({
                        header: header.parent,
                        x,
                        y: 0,
                        width: parentWidth,
                        height: ROW_HEIGHT,
                        index,
                    });
                }
                tmp.push({
                    header,
                    x,
                    y: ROW_HEIGHT,
                    width: width,
                    height: ROW_HEIGHT,
                    index,
                });
            } else {
                tmp.push({
                    header,
                    x,
                    y: 0,
                    width: width,
                    height: maxY * ROW_HEIGHT,
                    index,
                });
            }
            x += width;
        }
        return tmp;
    }, [headers, headersHeight, showIndex, onlyHeaders, getColumnSize]);

    const headerRenderer = useCallback(
        ({index, key, style}) => {
            // console.log(SQLTableHeaderCell);
            // return null;
            if (index < offset) return null;

            const gridItem = headerGrid[index];
            const header = gridItem?.header;
            if (!header) return null;
            const isLast = index === headers.length - 1;

            const sortIndex = sort.findIndex(
                (item) => item.column === header.field
            );
            const sortType = sortIndex !== -1 ? sort[sortIndex].type : null;

            if (header.isIndex) {
                return (
                    <SQLTableHeaderCell
                        isVertical={isVertical}
                        header={header}
                        key={key}
                        style={style}
                        isLast={isLast}
                        index={gridItem.index}
                        onResize={onResize}
                        container={container}
                        filters={filters}
                        setFilters={setFilters}
                    />
                );
            }
            return (
                <SQLTableHeaderCell
                    isVertical={isVertical}
                    header={header}
                    key={key}
                    style={style}
                    isLast={isLast}
                    index={gridItem.index}
                    onResize={onResize}
                    container={container}
                    setSort={setSort}
                    sortType={sortType}
                    sortIndex={sortIndex}
                    filters={filters}
                    setFilters={setFilters}
                />
            );
        },
        [
            headers,
            onResize,
            sort,
            container,
            setSort,
            offset,
            headerGrid,
            isVertical,
            filters,
            setFilters,
        ]
    );

    const cellRenderer = useCallback(
        ({key, style, columnIndex, rowIndex, ...props}) => {
            let cIndex = onlyHeaders ? columnIndex : columnIndex - showIndex;
            if (cIndex < offset) return null;

            const header = headers[cIndex];
            if (!header) return null;
            const isLast = cIndex === headers.length - 1;

            if (header.isIndex) {
                return (
                    <SQLTableItemCell
                        isVertical={isVertical}
                        key={key}
                        style={style}
                        header={header}
                        row={{
                            index: rowIndex + 1,
                            ...data[sortedData[rowIndex].rowIndex],
                        }}
                        isLast={isLast}
                        rowIndex={rowIndex}
                        colIndex={columnIndex}
                        onClick={onClick}
                        getCellClassName={getCellClassName}
                    />
                );
            }

            // wyciaganie wiersza ze zwyklej tablicy na podstawie sortowanej tablicy
            return (
                <SQLTableItemCell
                    isVertical={isVertical}
                    key={key}
                    style={style}
                    header={header}
                    row={data[sortedData[rowIndex].rowIndex]}
                    isLast={isLast}
                    rowIndex={rowIndex}
                    colIndex={columnIndex}
                    onClick={onClick}
                    getCellClassName={getCellClassName}
                />
            );
        },
        [
            headers,
            sortedData,
            data,
            offset,
            onClick,
            getCellClassName,
            showIndex,
            onlyHeaders,
            isVertical,
        ]
    );

    const recomputeGrid = useCallback(() => {
        headerApi.current.recomputeCellSizesAndPositions();
        gridApi.current.recomputeGridSize();
    }, []);

    useImperativeHandle(ref, () => {
        return {
            recomputeGrid,
            gridApi,
            headerApi,
        };
    });

    const cellSizeAndPositionGetter = useCallback(
        ({index}) => {
            let tmp = headerGrid[index];
            return {
                width: tmp.width,
                height: tmp.height,
                x: tmp.x,
                y: tmp.y,
            };
        },
        [headerGrid]
    );

    return (
        <>
            <div style={{width}}>
                <Collection
                    cellCount={headerGrid.length}
                    cellRenderer={headerRenderer}
                    cellSizeAndPositionGetter={cellSizeAndPositionGetter}
                    height={headersHeight}
                    width={width - headerOffset}
                    scrollLeft={scrollLeft}
                    className="sql-table-header-row"
                    ref={headerApi}
                />
            </div>
            <Grid
                cellRenderer={cellRenderer}
                columnCount={
                    onlyHeaders ? headers.length : headers.length + +showIndex
                }
                columnWidth={getColumnSize}
                height={height - headersHeight}
                rowCount={sortedData.length}
                rowHeight={ROW_HEIGHT}
                width={width}
                ref={gridApi}
                onScroll={onScroll}
                scrollLeft={scrollLeft}
                scrollTop={scrollTop}
            />
        </>
    );
});

function SQLTableRenderer({
    frozen,
    frozenWidth,
    columnSize,
    container,
    data,
    onResize,
    setSort,
    sort,
    sortedData,
    showIndex,
    getCellClassName,
    onClick,
    sortedHeaders,
    scrollData,
    onScrollNormal,
    onScrollFrozen,
    bottomPinnedData,
    isOverflowing,
    isVertical,
    filters,
    setFilters,
}) {
    const frozenContainer = useRef();
    const normalContainer = useRef();

    useEffect(() => {
        if (frozenContainer.current) {
            frozenContainer.current.recomputeGrid();
        }
        if (normalContainer.current) {
            normalContainer.current.recomputeGrid();
        }
    }, [columnSize]);

    const calculateFrozenHeight = useCallback(
        (height) => {
            if (!isVertical && bottomPinnedData) {
                if (!isOverflowing)
                    return height - bottomPinnedData.length * ROW_HEIGHT;
                return height - bottomPinnedData.length * ROW_HEIGHT - 17;
            }
            if (isOverflowing) return height - 17;
            return height;
        },
        [isOverflowing, bottomPinnedData, isVertical]
    );

    const calculateGridHeight = useCallback(
        (height) => {
            // if (isOverflowing && bottomPinnedData) return height - (bottomPinnedData.length * ROW_HEIGHT) + 17;
            if (isVertical) {
                return height;
            }
            if (bottomPinnedData) {
                return height - bottomPinnedData.length * ROW_HEIGHT;
            }
            return height;
        },
        [bottomPinnedData, isVertical]
    );

    const headersHeight = useMemo(() => {
        if (sortedHeaders.some((item) => !!item.parent)) return ROW_HEIGHT * 2;
        return ROW_HEIGHT;
    }, [sortedHeaders]);

    return (
        <>
            {frozen.length > 0 && (
                <div
                    style={{flex: `0 0 ${frozenWidth}`}}
                    className="sql-table-frozen">
                    <AutoSizer disableWidth>
                        {({height}) => (
                            <SQLTableGridRenderer
                                isVertical={isVertical}
                                columnSize={columnSize}
                                container={container}
                                data={data}
                                headers={frozen}
                                height={calculateFrozenHeight(height)}
                                onResize={onResize}
                                setSort={setSort}
                                sort={sort}
                                sortedData={sortedData}
                                width={frozenWidth}
                                ref={frozenContainer}
                                scrollTop={scrollData.scrollTop}
                                scrollLeft={0}
                                onScroll={onScrollFrozen}
                                onClick={onClick}
                                getCellClassName={getCellClassName}
                                headersHeight={headersHeight}
                                filters={filters}
                                setFilters={setFilters}
                            />
                        )}
                    </AutoSizer>
                </div>
            )}
            <div className="sql-table-grid">
                <AutoSizer>
                    {({height, width}) => (
                        <SQLTableGridRenderer
                            isVertical={isVertical}
                            columnSize={columnSize}
                            container={container}
                            data={data}
                            headers={sortedHeaders}
                            height={calculateGridHeight(height)}
                            onResize={onResize}
                            setSort={setSort}
                            sort={sort}
                            sortedData={sortedData}
                            width={width}
                            offset={
                                frozen.length === 0
                                    ? 0
                                    : frozen.length - showIndex
                            }
                            headerOffset={17}
                            ref={normalContainer}
                            scrollTop={scrollData.scrollTop}
                            scrollLeft={scrollData.scrollLeft}
                            onScroll={onScrollNormal}
                            onClick={onClick}
                            getCellClassName={getCellClassName}
                            showIndex={showIndex}
                            onlyHeaders={frozen.length === 0}
                            headersHeight={headersHeight}
                            filters={filters}
                            setFilters={setFilters}
                        />
                    )}
                </AutoSizer>
            </div>
        </>
    );
}

function SQLTableBottomRowsRenderer({
    columnSize,
    showIndex,
    offset = 0,
    headers,
    getColumnSize,
    height,
    bottomPinnedData,
    width,
    scrollLeft,
    onScroll,
    isOverflowing,
    onlyHeaders,
}) {
    const gridApi = useRef();

    useEffect(() => {
        if (gridApi.current) {
            gridApi.current.recomputeCellSizesAndPositions();
        }
    }, [columnSize, isOverflowing]);

    const collectionData = useMemo(() => {
        const tmp = [];
        for (let i = 0; i < bottomPinnedData.length; i++) {
            let x = 0;
            let y = i * ROW_HEIGHT;
            if (!onlyHeaders && showIndex) {
                const width = getColumnSize({index: 0});
                tmp.push({
                    header: null,
                    x,
                    y,
                    width,
                    height: ROW_HEIGHT,
                    index: 0,
                    value: null,
                });
                x += width;
            }
            for (let j = 0; j < headers.length; j++) {
                const index = !onlyHeaders && showIndex ? j + 1 : j;
                const header = headers[j];
                const prevHeader = headers[j - 1];
                if (header.parent && prevHeader?.parent !== header.parent) {
                    let width = 0;
                    for (let k = 0; k < header.parent.children.length; k++) {
                        width += getColumnSize({index: index + k});
                    }
                    tmp.push({
                        header: header.parent,
                        x,
                        y,
                        width,
                        height: ROW_HEIGHT,
                        index,
                        value: bottomPinnedData[i][header.parent.field],
                    });
                    x += width;
                } else if (
                    !header.parent ||
                    header.parent !== prevHeader?.parent
                ) {
                    const width = getColumnSize({index});
                    tmp.push({
                        header,
                        x,
                        y,
                        width,
                        height: ROW_HEIGHT,
                        index,
                        value: bottomPinnedData[i][header.field],
                    });
                    x += width;
                }
            }
        }
        return tmp;
    }, [headers, getColumnSize, onlyHeaders, showIndex, bottomPinnedData]);

    const cellRenderer = useCallback(
        ({key, style, index}) => {
            let cIndex = index;
            if (cIndex < offset)
                return (
                    <div style={style} key={key} className="sql-table-cell" />
                );

            const data = collectionData[index];
            if (isNil(data?.value))
                return (
                    <div style={style} key={key} className="sql-table-cell" />
                );

            return (
                <div style={style} key={key} className="sql-table-cell">
                    {data.value}
                </div>
            );
        },
        [collectionData, offset]
    );

    const cellSizeAndPositionGetter = useCallback(
        ({index}) => {
            return collectionData[index];
        },
        [collectionData]
    );

    return (
        <Collection
            cellCount={collectionData.length}
            cellRenderer={cellRenderer}
            cellSizeAndPositionGetter={cellSizeAndPositionGetter}
            height={height}
            width={width}
            ref={gridApi}
            scrollLeft={scrollLeft}
            onScroll={onScroll}
        />
    );
}

function SQLTableBottomRows({
    frozen,
    bottomPinnedData,
    frozenWidth,
    headers,
    showIndex,
    columnSize,
    scrollData,
    onScroll,
    isOverflowing,
}) {
    const getColumnSize = useCallback(
        ({index}) => columnSize[index] || MIN_COLUMN_WIDTH,
        [columnSize]
    );

    return (
        <>
            {frozen.length > 0 && (
                <div
                    style={{flex: `0 0 ${frozenWidth}`}}
                    className="sql-table-frozen">
                    <SQLTableBottomRowsRenderer
                        bottomPinnedData={bottomPinnedData}
                        columnSize={columnSize}
                        getColumnSize={getColumnSize}
                        headers={frozen}
                        height={bottomPinnedData.length * ROW_HEIGHT}
                        scrollLeft={0}
                        showIndex={showIndex}
                        width={frozenWidth}
                        isOverflowing={isOverflowing}
                    />
                </div>
            )}
            <AutoSizer disableHeight>
                {({width}) => (
                    <SQLTableBottomRowsRenderer
                        bottomPinnedData={bottomPinnedData}
                        columnSize={columnSize}
                        getColumnSize={getColumnSize}
                        headers={headers}
                        height={
                            isOverflowing
                                ? bottomPinnedData.length * ROW_HEIGHT + 17
                                : bottomPinnedData.length * ROW_HEIGHT
                        }
                        onScroll={onScroll}
                        scrollLeft={scrollData.scrollLeft}
                        showIndex={showIndex}
                        width={isOverflowing ? width - 17 : width}
                        offset={frozen.length - showIndex}
                        onlyHeaders={frozen.length === 0}
                    />
                )}
            </AutoSizer>
        </>
    );
}

export default function SQLTable({
    headers: _headers,
    data: _data,
    showIndex = false,
    onClick: _onClick,
    getCellClassName: _getCellClassName,
    bottomPinnedRows,
    isVertical = false,
}) {
    const container = useRef();

    const bottomPinnedData = useMemo(() => {
        if (!bottomPinnedRows) return null;
        return bottomPinnedRows(_data, null, null, _data);
    }, [bottomPinnedRows, _data]);

    const [headers, data, onCellClick, getCellClassName] = useTableData(
        _data,
        _headers,
        isVertical,
        _onClick,
        _getCellClassName,
        bottomPinnedData
    );

    const columnsFlattened = useMemo(() => {
        const columns = [];
        for (let header of headers) {
            if (header.children) {
                columns.push(
                    ...header.children.map((item) => ({
                        ...item,
                        parent: header,
                    }))
                );
            } else {
                columns.push(header);
            }
        }
        return columns;
    }, [headers]);

    const [columnSize, setColumnSize] = useState(() => {
        const length = showIndex
            ? columnsFlattened.length + 1
            : columnsFlattened.length;
        return new Array(length).fill(MIN_COLUMN_WIDTH);
    });
    const [sort, setSort] = useState([]);
    const [scrollData, setScrollData] = useState({scrollTop: 0, scrollLeft: 0});
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [filters, setFilters] = useState({});

    useEffect(() => {
        const grid = container.current.querySelector(".ReactVirtualized__Grid");
        const isGridOverflowing = grid.scrollHeight > grid.clientHeight;
        const width = isGridOverflowing
            ? container.current.clientWidth - 17
            : container.current.clientWidth;
        const length = showIndex
            ? columnsFlattened.length + 1
            : columnsFlattened.length;
        let columnWidth = Math.floor(width / length) - 1.5; // prawdopodobnie 2 px na border
        if (columnWidth < MIN_COLUMN_WIDTH) columnWidth = MIN_COLUMN_WIDTH;
        if (columnWidth > MIN_COLUMN_WIDTH || columnSize.length < length) {
            setColumnSize(new Array(length).fill(columnWidth));
        }
    }, [columnsFlattened.length, showIndex]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (container.current) {
            const grid = container.current.querySelector(
                ".ReactVirtualized__Grid"
            );
            const isGridOverflowing = grid.scrollHeight > grid.clientHeight;
            const maxWidth = isGridOverflowing
                ? container.current.clientWidth - 17
                : container.current.clientWidth;
            if (columnSize.reduce((prev, curr) => prev + curr, 0) > maxWidth) {
                setIsOverflowing(true);
            } else {
                setIsOverflowing(false);
            }
        }
    }, [columnSize]);

    const {frozen, sortedHeaders, frozenWidth} = useMemo(() => {
        const frozenHeaders = columnsFlattened.filter((item) => item.frozen);
        if (frozenHeaders.length === columnsFlattened.length) {
            return {
                frozen: [],
                sortedHeaders: showIndex
                    ? [
                          {
                              isIndex: true,
                              name: "#",
                              field: "index",
                          },
                          ...columnsFlattened,
                      ]
                    : columnsFlattened,
                frozenWidth: 0,
            };
        }
        const frozen = showIndex
            ? [{isIndex: true, name: "#", field: "index"}, ...frozenHeaders]
            : frozenHeaders;
        const frozenColumnSize = columnSize.slice(0, frozen.length);
        const width = frozenColumnSize.reduce((prev, curr) => prev + curr, 0);
        const maxWidth = container.current?.clientWidth / 2;
        // zamrozone kolumny mieszcza sie w kontenerze
        if (width < maxWidth) {
            return {
                frozen,
                sortedHeaders: columnsFlattened.sort(
                    (a, b) => b.frozen - a.frozen
                ),
                frozenWidth: width,
            };
        }
        // zamrozone kolumny nie mieszcza sie w kontenerze wiec trzeba je przyciac
        let newWidth = 0;
        let newFrozen = [];
        for (let i = 0; i < frozen.length; i++) {
            if (newWidth + columnSize[i] > maxWidth) break;
            newWidth += columnSize[i];
            newFrozen.push(frozen[i]);
        }
        if (newFrozen.length === 0 || (newFrozen.length === 1 && showIndex)) {
            return {
                frozen: [],
                sortedHeaders: showIndex
                    ? [
                          {
                              isIndex: true,
                              name: "#",
                              field: "index",
                          },
                          ...columnsFlattened,
                      ]
                    : columnsFlattened,
                frozenWidth: 0,
            };
        }
        return {
            frozen: newFrozen,
            sortedHeaders: columnsFlattened.sort((a, b) => b.frozen - a.frozen),
            frozenWidth: newWidth,
        };
    }, [columnsFlattened, showIndex, columnSize, container.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const dataForSort = useMemo(() => {
        // tworzenie tablicy dla sortowania
        return data.map((row, index) => {
            const obj = {rowIndex: index, _row: row};
            for (let {
                field,
                valueFormatter,
                sortFormatter,
            } of columnsFlattened) {
                if (sortFormatter) {
                    obj[field] = sortFormatter(row[field]);
                } else if (valueFormatter) {
                    obj[field] = valueFormatter(row[field]);
                } else {
                    obj[field] = row[field];
                }
            }
            return obj;
        });
    }, [data, columnsFlattened]);

    const filteredData = useMemo(() => {
        if (Object.keys(filters).length === 0) return dataForSort;
        return dataForSort.filter((item) => {
            for (let key in filters) {
                const {value, type} = filters[key];
                const rowValue = item[key];
                if (isNil(rowValue)) return false;
                if (type === FilterTypes.STRING) {
                    // uzytkownik moze wpisac jedna wartosc lub rozdzielic je znakiem |, nie wazne czy wstawi spacje
                    const tokens = value.split("|");
                    let hasAnyToken = false;
                    for (let token of tokens) {
                        if ((rowValue + "").includes(token.trim()))
                            hasAnyToken = true;
                    }
                    if (!hasAnyToken) return false;
                } else if (type === FilterTypes.NUMBER) {
                    // sprawdzanie dla wartosci [x-y], (x-y), [x-y) i (x-y]
                    let rangeMatch = value.match(rangeRegex);
                    if (rangeMatch) {
                        let lowerBound = parseFloat(rangeMatch[2]);
                        let upperBound = parseFloat(rangeMatch[3]);
                        let includeLower = rangeMatch[1] === "[";
                        let includeUpper = rangeMatch[4] === "]";
                        if (includeLower) {
                            if (rowValue < lowerBound) return false;
                        } else {
                            if (rowValue <= lowerBound) return false;
                        }
                        if (includeUpper) {
                            if (rowValue > upperBound) return false;
                        } else {
                            if (rowValue >= upperBound) return false;
                        }
                        return true;
                    }
                    // wyszukiwanie operatora > < <= >= = <>
                    let operationMatch = value.match(operationRegex);
                    let operator = operationMatch?.[1] || "=";
                    let operationValue = parseFloat(
                        operationMatch?.[2] || value
                    );
                    switch (operator) {
                        case "=":
                            if (+rowValue !== operationValue) return false;
                            break;
                        case ">":
                            if (+rowValue <= operationValue) return false;
                            break;
                        case "<":
                            if (+rowValue >= operationValue) return false;
                            break;
                        case ">=":
                            if (+rowValue < operationValue) return false;
                            break;
                        case "<=":
                            if (+rowValue > operationValue) return false;
                            break;
                        case "<>":
                            if (+rowValue === operationValue) return false;
                            break;
                        default:
                            break;
                    }
                } else if (type === FilterTypes.DATE) {
                    const dateValue = item._row[key];
                    const dates = value.match(dateRegex);
                    if (dates.length > 1) {
                        const lowerBound = parseDate(dates[0]);
                        const upperBound = parseDate(dates[1]);
                        if (!lowerBound || !upperBound) return false; // blad parsowania daty
                        let includeLower = value.startsWith("[");
                        let includeUpper = value.trim().endsWith("]");
                        if (includeLower) {
                            if (moment(dateValue).isBefore(lowerBound, "day"))
                                return false;
                        } else if (value.startsWith("(")) {
                            if (
                                moment(dateValue).isSameOrBefore(
                                    lowerBound,
                                    "day"
                                )
                            )
                                return false;
                        } else {
                            return false;
                        }
                        if (includeUpper) {
                            if (moment(dateValue).isAfter(upperBound, "day"))
                                return false;
                        } else if (value.trim().endsWith(")")) {
                            if (
                                moment(dateValue).isSameOrAfter(
                                    upperBound,
                                    "day"
                                )
                            )
                                return false;
                        } else {
                            return false;
                        }
                        return true;
                    }
                    let operationMatch = value.match(operationDateRegex);
                    let operator = operationMatch?.[1] || "=";
                    let dateString = operationMatch
                        ? `${operationMatch[2]}-${operationMatch[3]}-${operationMatch[4]}`
                        : value;
                    let operationValue = parseDate(dateString);
                    if (!operationValue) return false;
                    switch (operator) {
                        case "=":
                            if (
                                !moment(dateValue).isSame(operationValue, "day")
                            )
                                return false;
                            break;
                        case ">":
                            if (
                                !moment(dateValue).isAfter(
                                    operationValue,
                                    "day"
                                )
                            )
                                return false;
                            break;
                        case "<":
                            if (
                                !moment(dateValue).isBefore(
                                    operationValue,
                                    "day"
                                )
                            )
                                return false;
                            break;
                        case ">=":
                            if (
                                !moment(dateValue).isSameOrAfter(
                                    operationValue,
                                    "day"
                                )
                            )
                                return false;
                            break;
                        case "<=":
                            if (
                                !moment(dateValue).isSameOrBefore(
                                    operationValue,
                                    "day"
                                )
                            )
                                return false;
                            break;
                        case "<>":
                            if (moment(dateValue).isSame(operationValue, "day"))
                                return false;
                            break;
                        default:
                            break;
                    }
                } else if (type === FilterTypes.BOOLEAN) {
                    if (rowValue !== value) return false;
                    return true;
                } else if (type === "dictionary") {
                    if (!value || value.length === 0) return true;
                    const notFormatterRowValue = item._row[key];
                    return value.includes(notFormatterRowValue);
                }
            }
            return true;
        });
    }, [dataForSort, filters]);

    const sortedData = useMemo(() => {
        if (sort.length === 0) return filteredData;

        return filteredData.slice().sort((a, b) => {
            for (let {column, type} of sort) {
                const aField = a[column];
                const bField = b[column];
                if (aField === null || aField === undefined) return 1;
                if (bField === null || bField === undefined) return -1;
                if (aField !== bField) {
                    if (
                        typeof aField === "string" &&
                        typeof bField === "string"
                    ) {
                        if (type === "ASC")
                            return aField.localeCompare(bField, undefined, {
                                numeric: true,
                            });
                        return bField.localeCompare(aField, undefined, {
                            numeric: true,
                        });
                    }
                    if (type === "ASC") return aField > bField ? 1 : -1;
                    return aField < bField ? 1 : -1;
                }
            }
            return 0;
        });
    }, [filteredData, sort]);

    const onResize = useCallback((columnIndex, size, scrollLeft = 0) => {
        setColumnSize((prev) => {
            const newArray = prev.slice();
            let newSize =
                size -
                newArray
                    .slice(0, columnIndex)
                    .reduce((prev, curr) => prev + curr, 0) +
                scrollLeft;
            newArray[columnIndex] =
                newSize > MIN_COLUMN_WIDTH ? newSize : MIN_COLUMN_WIDTH;
            return newArray;
        });
    }, []);

    const onScrollNormal = useCallback(({scrollTop, scrollLeft}) => {
        setScrollData({scrollTop, scrollLeft});
    }, []);

    const onScrollFrozen = useCallback(({scrollTop}) => {
        setScrollData((prev) => ({...prev, scrollTop}));
    }, []);

    const onScrollBottomPinned = useCallback(({scrollLeft}) => {
        setScrollData((prev) => ({...prev, scrollLeft}));
    }, []);

    return (
        <div
            className={`sql-table${bottomPinnedData ? " bottom-pinned" : ""}`}
            ref={container}>
            <SQLTableRenderer
                columnSize={columnSize}
                container={container}
                isVertical={isVertical}
                data={data}
                frozen={frozen}
                frozenWidth={frozenWidth}
                getCellClassName={getCellClassName}
                onClick={onCellClick}
                onResize={onResize}
                setSort={isVertical ? null : setSort}
                showIndex={showIndex}
                sort={sort}
                sortedData={sortedData}
                sortedHeaders={sortedHeaders}
                onScrollNormal={onScrollNormal}
                onScrollFrozen={onScrollFrozen}
                scrollData={scrollData}
                bottomPinnedData={isVertical ? null : bottomPinnedData}
                isOverflowing={isOverflowing}
                filters={filters}
                setFilters={setFilters}
            />
            {!isVertical && !!bottomPinnedData?.length && (
                <div className="sql-table-bottom-pinned">
                    <SQLTableBottomRows
                        bottomPinnedData={bottomPinnedData}
                        frozen={frozen}
                        frozenWidth={frozenWidth}
                        headers={sortedHeaders}
                        onScroll={onScrollBottomPinned}
                        showIndex={showIndex}
                        scrollData={scrollData}
                        columnSize={columnSize}
                        container={container}
                        isOverflowing={isOverflowing}
                    />
                </div>
            )}
        </div>
    );
}
